import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, FloppyDisk, EnvelopeSimple, X as XIcon, Star, PaperPlaneTilt } from "@phosphor-icons/react";

const EMPTY = { name: "", subject: "", body_html: "", default_to: "", default_cc: "", default_bcc: "" };
const DEFAULTS = {
  ddt: {
    subject: "[Powercom Magazzino] DDT {{ddt_number}} emesso",
    body: (
      "<h2 style='color:#002FA7;margin:0 0 12px 0'>DDT emesso</h2>\n" +
      "<p>Il DDT <b>{{ddt_number}}</b> destinato a <b>{{customer_name}}</b> è stato firmato ed emesso.</p>\n" +
      "<p><b>Dettaglio materiali consegnati:</b></p>\n" +
      "{{items_table_html}}\n" +
      "<p>In allegato trovi il DDT sottoscritto in PDF.</p>\n" +
      "<p style='color:#666;font-size:12px'>{{timestamp}}</p>"
    ),
    variables: ["ddt_number", "customer_name", "customer_email", "product_sku",
                "product_name", "quantity", "unit_of_measure", "items_table_html", "timestamp"],
    rebindMsg: (n) => n ? ` · ${n} clienti riagganciati` : "",
    deleteNotice: "I clienti che lo usano verranno riagganciati al template predefinito.",
  },
  transfer: {
    subject: "[Powercom Magazzino] Trasferimento verso {{to_warehouse}}",
    body: (
      "<h2 style='color:#002FA7;margin:0 0 12px 0'>Trasferimento merce</h2>\n" +
      "<p>Ciao <b>{{technician_name}}</b>, è stato firmato un trasferimento merce.</p>\n" +
      "<table style='border-collapse:collapse;margin:12px 0;font-size:14px'>\n" +
      "<tr><td style='padding:4px 8px;color:#666'>Da:</td><td style='padding:4px 8px'><b>{{from_warehouse}}</b></td></tr>\n" +
      "<tr><td style='padding:4px 8px;color:#666'>A:</td><td style='padding:4px 8px'><b>{{to_warehouse}}</b></td></tr>\n" +
      "<tr><td style='padding:4px 8px;color:#666'>Data:</td><td style='padding:4px 8px'>{{timestamp}}</td></tr>\n" +
      "</table>\n" +
      "<p><b>Dettaglio materiali trasferiti:</b></p>\n" +
      "{{items_table_html}}\n" +
      "<p>In allegato trovi il documento di trasferimento firmato in PDF.</p>"
    ),
    variables: ["technician_name", "from_warehouse", "to_warehouse", "product_sku",
                "product_name", "quantity", "unit_of_measure", "items_table_html", "timestamp"],
    rebindMsg: () => "",  // transfers aren't bound to customers
    deleteNotice: "Il template verrà rimosso dal sistema.",
  },
};
const splitEmails = (s) => (s || "").split(/[,;\n]/).map((x) => x.trim()).filter(Boolean);

export default function SettingsDDTTemplates({ kind = "ddt" }) {
  const cfg = DEFAULTS[kind] || DEFAULTS.ddt;
  const DEFAULT_SUBJECT = cfg.subject;
  const DEFAULT_BODY = cfg.body;
  const VARIABLES = cfg.variables;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | row | {new: true}
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get(`/ddt-templates?kind=${encodeURIComponent(kind)}`); setRows(data || []); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [kind]);

  const openNew = () => {
    setEditing({ new: true });
    setForm({ ...EMPTY, subject: DEFAULT_SUBJECT, body_html: DEFAULT_BODY });
  };
  const openEdit = (r) => {
    setEditing(r);
    setForm({
      name: r.name || "",
      subject: r.subject || "",
      body_html: r.body_html || "",
      default_to: (r.default_to || []).join(", "),
      default_cc: (r.default_cc || []).join(", "),
      default_bcc: (r.default_bcc || []).join(", "),
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Nome obbligatorio"); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        subject: form.subject,
        body_html: form.body_html,
        default_to: splitEmails(form.default_to),
        default_cc: splitEmails(form.default_cc),
        default_bcc: splitEmails(form.default_bcc),
        kind,
      };
      if (editing?.new) await api.post("/ddt-templates", payload);
      else await api.put(`/ddt-templates/${editing.id}`, payload);
      toast.success("Template salvato");
      setEditing(null); setForm(EMPTY); load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const remove = async (r) => {
    if (r.is_default) { toast.error("Il template predefinito non può essere eliminato"); return; }
    if (!window.confirm(`Eliminare il template "${r.name}"? ${cfg.deleteNotice}`)) return;
    try {
      const { data } = await api.delete(`/ddt-templates/${r.id}`);
      toast.success(`Template eliminato${cfg.rebindMsg(data.customers_rebound)}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const sendTest = async () => {
    if (!editing || editing?.new) {
      toast.error("Salva il template prima di inviare un test");
      return;
    }
    const to = window.prompt(
      "Email destinatario per il test:\n(il template verrà renderizzato con dati di esempio e un banner 'EMAIL DI PROVA')",
      ""
    );
    if (!to) return;
    try {
      const { data } = await api.post(`/ddt-templates/${editing.id}/test-send`, { to });
      toast.success(`Email di prova inviata a ${(data.to || []).join(", ")} via ${data.provider}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (loading) return <div className="p-6 text-sm text-neutral-500">Caricamento…</div>;

  return (
    <section className="space-y-4" data-testid="settings-ddt-templates">
      <div className="border border-neutral-200 bg-white p-5">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <div className="label-eyebrow text-[#002FA7] mb-1">
              {kind === "transfer" ? "Trasferimento merce" : "DDT Emesso"}
            </div>
            <h3 className="font-display font-bold text-lg tracking-tight">
              {kind === "transfer" ? "Template email per trasferimenti" : "Template email per DDT"}
            </h3>
            <p className="text-sm text-neutral-600 mt-1 max-w-2xl">
              {kind === "transfer"
                ? "L'email di trasferimento parte alla firma del movimento quando almeno un magazzino coinvolto è un furgone/mobile. Il tecnico (proprietario del furgone) riceve il PDF firmato in allegato; chi firma dal centrale va in Cc. Puoi creare più template per usi diversi — il predefinito è il fallback."
                : "Ogni cliente può essere associato a un template specifico. L'email \"DDT emesso\" (automatica e manuale) userà oggetto, corpo e destinatari di quel template. Il template predefinito è il fallback universale."
              }
            </p>
          </div>
          <button type="button" onClick={openNew} data-testid="ddt-tpl-new"
            className="inline-flex items-center gap-1.5 px-3 py-2 bg-[#002FA7] text-white hover:bg-[#0038CC] text-sm">
            <Plus size={14} weight="bold" /> Nuovo
          </button>
        </div>

        <div className="border border-neutral-200">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 text-neutral-600 uppercase text-[11px] tracking-wider border-b border-neutral-200">
              <tr>
                <th className="px-4 py-2 text-left">Nome</th>
                <th className="px-4 py-2 text-left">Oggetto</th>
                <th className="px-4 py-2 text-left">Destinatari (To · CC · BCC)</th>
                <th className="px-4 py-2 w-[120px]"></th>
              </tr>
            </thead>
            <tbody data-testid="ddt-tpl-rows">
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-neutral-100 hover:bg-neutral-50">
                  <td className="px-4 py-2 font-medium">
                    <div className="flex items-center gap-1.5">
                      {r.is_default && <Star size={14} weight="fill" className="text-amber-500" title="Predefinito" />}
                      {r.name}
                    </div>
                  </td>
                  <td className="px-4 py-2 text-neutral-600 text-xs truncate max-w-[300px]">{r.subject}</td>
                  <td className="px-4 py-2 text-xs text-neutral-600">
                    <div className="font-mono space-y-0.5">
                      {r.default_to?.length > 0 && <div><span className="text-neutral-400">To:</span> {r.default_to.join(", ")}</div>}
                      {r.default_cc?.length > 0 && <div><span className="text-neutral-400">Cc:</span> {r.default_cc.join(", ")}</div>}
                      {r.default_bcc?.length > 0 && <div><span className="text-neutral-400">Bcc:</span> {r.default_bcc.join(", ")}</div>}
                      {!r.default_to?.length && !r.default_cc?.length && !r.default_bcc?.length && (
                        <span className="text-neutral-400 italic font-sans">— (usa email del cliente)</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-2 text-right">
                    <button type="button" onClick={() => openEdit(r)} data-testid={`ddt-tpl-edit-${r.id}`}
                      className="inline-flex items-center p-1.5 text-neutral-600 hover:text-[#002FA7] border border-neutral-300 mr-1">
                      <PencilSimple size={13} />
                    </button>
                    <button type="button" onClick={() => remove(r)} disabled={r.is_default} data-testid={`ddt-tpl-del-${r.id}`}
                      title={r.is_default ? "Non eliminabile" : "Elimina"}
                      className={`inline-flex items-center p-1.5 border ${r.is_default ? "border-neutral-200 text-neutral-300 cursor-not-allowed" : "border-red-300 text-red-600 hover:bg-red-50"}`}>
                      <Trash size={13} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <form onSubmit={submit} onClick={(e) => e.stopPropagation()}
            className="bg-white border border-neutral-200 shadow-xl w-full max-w-2xl max-h-[95vh] flex flex-col overflow-y-auto"
            data-testid="ddt-tpl-form">
            <div className="flex items-center justify-between px-5 py-3 border-b border-neutral-200 bg-neutral-50">
              <div className="flex items-center gap-2">
                <EnvelopeSimple size={20} weight="bold" className="text-[#002FA7]" />
                <h3 className="font-display font-bold">{editing?.new ? "Nuovo template DDT" : `Modifica · ${editing.name}`}</h3>
                {editing?.is_default && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 border border-amber-300 bg-amber-50 text-amber-700 text-xs">
                    <Star size={10} weight="fill" /> PREDEFINITO
                  </span>
                )}
              </div>
              <button type="button" onClick={() => setEditing(null)} className="p-1 hover:bg-neutral-200"><XIcon size={18} /></button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="label-eyebrow block mb-1">Nome del template</label>
                <input required data-testid="ddt-tpl-name"
                  value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="es. DDT TIM, DDT WindTre, DDT emesso (predefinito)"
                  className="w-full px-3 py-2 border border-neutral-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
              </div>
              <div>
                <label className="label-eyebrow block mb-1">Oggetto email</label>
                <input data-testid="ddt-tpl-subject"
                  value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })}
                  className="w-full px-3 py-2 border border-neutral-300 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
              </div>
              <div>
                <label className="label-eyebrow block mb-1">Corpo HTML</label>
                <textarea required rows={10} data-testid="ddt-tpl-body"
                  value={form.body_html} onChange={(e) => setForm({ ...form, body_html: e.target.value })}
                  className="w-full px-3 py-2 border border-neutral-300 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
                <div className="text-xs text-neutral-500 mt-1">
                  Variabili disponibili: {VARIABLES.map((v) => (
                    <code key={v} className="inline-block mr-1 px-1 bg-neutral-100 border border-neutral-200">{`{{${v}}}`}</code>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="label-eyebrow block mb-1">To (Destinatari)</label>
                  <textarea data-testid="ddt-tpl-to" rows={3}
                    value={form.default_to} onChange={(e) => setForm({ ...form, default_to: e.target.value })}
                    placeholder="separati da virgola, ; o newline"
                    className="w-full px-3 py-2 border border-neutral-300 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
                </div>
                <div>
                  <label className="label-eyebrow block mb-1">Cc</label>
                  <textarea data-testid="ddt-tpl-cc" rows={3}
                    value={form.default_cc} onChange={(e) => setForm({ ...form, default_cc: e.target.value })}
                    className="w-full px-3 py-2 border border-neutral-300 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
                </div>
                <div>
                  <label className="label-eyebrow block mb-1">Bcc</label>
                  <textarea data-testid="ddt-tpl-bcc" rows={3}
                    value={form.default_bcc} onChange={(e) => setForm({ ...form, default_bcc: e.target.value })}
                    className="w-full px-3 py-2 border border-neutral-300 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
                </div>
              </div>
              <div className="text-xs text-neutral-500 bg-neutral-50 border border-neutral-200 p-3">
                <b>Suggerimento:</b> se lasci "To" vuoto, l'email verrà inviata all'indirizzo del cliente (campo "email" nella scheda cliente).
              </div>
            </div>
            <div className="px-5 py-3 border-t border-neutral-200 bg-neutral-50 flex justify-between items-center gap-2 flex-wrap">
              <div>
                {editing?.new ? (
                  <span className="text-xs text-neutral-500 italic">Salva il template per inviare una prova</span>
                ) : (
                  <button type="button" onClick={sendTest} data-testid="ddt-tpl-test"
                    className="inline-flex items-center gap-2 px-3 py-2 border border-[#002FA7] text-[#002FA7] hover:bg-[#002FA7]/5 text-sm">
                    <PaperPlaneTilt size={14} weight="bold" /> Invia email di prova
                  </button>
                )}
              </div>
              <div className="flex gap-2">
                <button type="button" onClick={() => setEditing(null)}
                  className="px-4 py-2 text-sm border border-neutral-300 hover:bg-neutral-100">Annulla</button>
                <button type="submit" disabled={saving} data-testid="ddt-tpl-save"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-[#002FA7] text-white hover:bg-[#0038CC] disabled:opacity-40 text-sm">
                  <FloppyDisk size={14} weight="bold" /> {saving ? "Salvo…" : "Salva"}
                </button>
              </div>
            </div>
          </form>
        </div>
      )}
    </section>
  );
}
